import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { ChangeEvent, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  useDeleteRemainingCodes,
  useExtractRemainingCodes,
  useGetCsvDownload,
  useGetProductInventory,
  usePutCsvUpload,
} from "../../api/products";
import useNotify from "../../hooks/useNotify";
import { UserRoles } from "../../models/Authorisation";
import { PRODUCT_STATUS, ProductRequest } from "../../models/Product";
import { MODE, useFormStore } from "../../stores/formStore";
import { useUserStore } from "../../stores/userStore";
import { mono } from "../../theme/colors";
import Text from "../atoms/Text";
import ExclamationIcon from "../icons/ui/ExclamationIcon";
import DownloadIcon from "../icons/ui-actions/DownloadIcon";
import RefreshIcon from "../icons/ui-actions/RefreshIcon";
import UploadIcon from "../icons/ui-actions/UploadIcon";

type Props = {
  productStatus?: PRODUCT_STATUS;
};

const ProductInventory = ({ productStatus }: Props) => {
  const { productId } = useParams<{
    productId: string;
  }>();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const UploadableProductStatus = [
    PRODUCT_STATUS.DEACTIVATED,
    PRODUCT_STATUS.DRAFT,
    PRODUCT_STATUS.REJECTED,
  ];

  const mode = useFormStore(state => state.mode);

  const isEditMode = mode === MODE.EDIT;

  const isUploadDisabled =
    !productId ||
    (productStatus && !UploadableProductStatus.includes(productStatus)) ||
    !isEditMode;
  const isDisabled = !productId;
  const { data: inventory, refetch } = useGetProductInventory({ productId });
  const queryClient = useQueryClient();

  const { setNotifySuccess, setNotifyError } = useNotify();
  const formMethods = useFormContext<ProductRequest>();

  const { mutateAsync: getCsvExtract, isLoading: isExtracting } =
    useExtractRemainingCodes(productId);
  const { mutateAsync: getCodesDelete, isLoading: isDeleteloading } =
    useDeleteRemainingCodes(productId);
  const [isDeleteEnable, setIsDeleteEnable] = useState(true);

  const { mutateAsync: putCsvUpload, isLoading: isUploading } =
    usePutCsvUpload(productId);
  const { mutateAsync: getCsvDownload, isLoading: isDownloading } =
    useGetCsvDownload(productId);

  const onClickUpload = async () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const userInfo = useUserStore(state => state.user);

  const onClickDownload = async () => {
    try {
      await getCsvDownload();
    } catch (error) {
      setNotifyError("No downloadable file available");
    }
  };

  const onHandleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      const csvFile = event.target.files && event.target.files[0];

      if (!csvFile || !productId) {
        throw new Error("File or Product ID not found");
      }

      if (!csvFile.type.match("text/csv")) {
        throw new Error("File format must be .csv");
      }
      await putCsvUpload(csvFile);

      setNotifySuccess("The CSVS file have been auto saved.");
      await handleRefreshClick();
      await formMethods.trigger();
    } catch (error) {
      setNotifyError("Failed to upload file");
      console.error(error);
    }
  };

  const handleRefreshClick = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["product", "inventory", productId],
    });
  };

  const handleExtractRemainingCodesOnClick = async () => {
    try {
      await getCsvExtract();
      setIsDeleteEnable(false);
      setNotifySuccess("Extract remaining codes succeed!");
    } catch (error) {
      setNotifyError("Failed to extract remaining codes!");
    }
  };

  const handleDeleteRemainingCodesOnClick = async () => {
    try {
      await getCodesDelete();
      setNotifySuccess("Delete remaining codes succeed!");
      refetch();
      handleCloseModal();
    } catch (error) {
      setNotifyError("Failed to delete remaining codes!");
    }
  };

  return (
    <Stack
      direction={"column"}
      spacing={2}
      p={4}
      bgcolor="mono.white"
      borderRadius={4}
      width={379}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Text
          variant="title_small"
          color={isDisabled ? "mono.grey.2" : "mono.black"}
        >
          Product Inventory
        </Text>
        <IconButton
          data-testid="create_product-product_inventory-refresh-button"
          disabled={isDisabled}
          onClick={handleRefreshClick}
        >
          <RefreshIcon stroke={isDisabled ? mono.grey[2] : mono.black} />
        </IconButton>
      </Stack>
      <Stack
        borderRadius={2}
        px={2}
        py={1}
        direction={"row"}
        bgcolor={isDisabled ? mono.grey[1] : "primary.light"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Text
          variant="body_small"
          color={isDisabled ? "mono.grey.3" : "mono.black"}
        >
          Total Vouchers
        </Text>
        <Text
          variant="title_small"
          color={isDisabled ? "mono.grey.3" : "mono.black"}
        >
          {inventory?.total ?? "-"}
        </Text>
      </Stack>
      <Stack
        divider={
          <Box
            component="hr"
            sx={{
              width: "100%",
              border: "1px solid mono.grey.1",
            }}
          />
        }
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Type
          </Text>
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Amount
          </Text>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text
            variant="body_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Redeemed
          </Text>
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            {inventory?.redeemed ?? "-"}
          </Text>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text
            variant="body_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Remaining
          </Text>
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            {inventory?.remaining ?? "-"}
          </Text>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text
            variant="body_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Expired
          </Text>
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            {inventory?.expired ?? "-"}
          </Text>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text
            variant="body_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Earliest Expiry Date
          </Text>
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            {inventory?.earliestExpiryDate ?? "-"}
          </Text>
        </Stack>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text
            variant="body_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            Earliest Expiry Count
          </Text>
          <Text
            variant="title_small"
            color={isDisabled ? "mono.grey.2" : "mono.black"}
          >
            {inventory?.earliestExpiryCount ?? "-"}
          </Text>
        </Stack>

        <Box justifySelf={"stretch"} mt={2}>
          <Button
            data-testid="create_product-product_inventory-upload_csv-button"
            fullWidth
            variant={isUploadDisabled ? "disabled" : "secondary"}
            endIcon={
              isUploading ? (
                <CircularProgress size={18} />
              ) : (
                <UploadIcon
                  height={18}
                  stroke={isUploadDisabled ? mono.grey[3] : mono.black}
                />
              )
            }
            disabled={isUploadDisabled || isUploading}
            onClick={onClickUpload}
          >
            <Text variant="title_small" py={1}>
              Upload(CSVS)
            </Text>
          </Button>
          <input
            ref={inputRef}
            hidden
            type="file"
            aria-label="select files"
            multiple={false}
            accept={".csv"}
            onClick={(
              event: React.MouseEvent<HTMLInputElement, MouseEvent>,
            ) => {
              const element = event.target as HTMLInputElement;
              element.value = "";
            }}
            onChange={onHandleFileChange}
          />

          <Stack
            justifySelf={"stretch"}
            mt={2}
            sx={{
              borderTop: 0,
            }}
          >
            <Button
              data-testid="create_product-product_inventory-download_csv-button"
              fullWidth
              variant={isDisabled ? "disabled" : "secondary"}
              endIcon={
                isDownloading ? (
                  <CircularProgress size={18} />
                ) : (
                  <DownloadIcon
                    height={18}
                    stroke={isDisabled ? mono.grey[3] : mono.black}
                  />
                )
              }
              disabled={isDisabled || isDownloading}
              onClick={onClickDownload}
            >
              <Text variant="title_small" py={1}>
                Download(CSVS)
              </Text>
            </Button>
          </Stack>

          {inventory &&
            inventory?.remaining > 0 &&
            (productStatus === PRODUCT_STATUS.COMPLETE ||
              productStatus === PRODUCT_STATUS.DEACTIVATED) && (
              <>
                <Stack
                  justifySelf={"stretch"}
                  mt={2}
                  sx={{
                    borderTop: 0,
                  }}
                >
                  <Button
                    data-testid="create_product-product_inventory-extract_remaining_codes-button"
                    fullWidth
                    variant={isExtracting ? "disabled" : "secondary"}
                    endIcon={isExtracting && <CircularProgress size={18} />}
                    disabled={isExtracting}
                    onClick={handleExtractRemainingCodesOnClick}
                  >
                    <Text variant="title_small" py={1}>
                      Extract remaining codes
                    </Text>
                  </Button>
                </Stack>
                {userInfo && userInfo.role === UserRoles.PM && (
                  <Stack
                    justifySelf={"stretch"}
                    mt={2}
                    sx={{
                      borderTop: 0,
                    }}
                  >
                    <Button
                      data-testid="create_product-product_inventory-delete_remaining_codes-button"
                      fullWidth
                      variant={
                        isDeleteloading || isDeleteEnable
                          ? "disabled"
                          : "secondary"
                      }
                      endIcon={
                        isDeleteloading && <CircularProgress size={18} />
                      }
                      disabled={isDeleteloading || isDeleteEnable}
                      onClick={handleOpenModal}
                    >
                      <Text variant="title_small" py={1}>
                        Delete remaining codes
                      </Text>
                    </Button>
                  </Stack>
                )}
              </>
            )}
        </Box>
      </Stack>
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="modal-component"
      >
        <Box
          width={532}
          bgcolor="mono.white"
          borderRadius={2.5}
          pb={3}
          overflow="hidden"
        >
          <Box px={2.5} py={2} bgcolor="mono.light.grey">
            <Text variant={"title_medium"}>Delete Item</Text>
          </Box>

          <Stack p={3} alignItems="center">
            <ExclamationIcon width={48} height={48} />

            <Stack mt={2} mb={4} alignItems="center">
              <Text variant={"body_large"}>
                Are you sure you want to delete the remaining codes for this
                product?
              </Text>
            </Stack>

            <Stack direction="row" spacing={1} width={256}>
              <Button
                variant="secondary"
                size="small"
                fullWidth={true}
                onClick={handleCloseModal}
                data-testid="close-modal-button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                fullWidth={true}
                disabled={isDeleteloading}
                startIcon={isDeleteloading && <CircularProgress size={20} />}
                onClick={handleDeleteRemainingCodesOnClick}
                data-testid="delete-button"
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Stack>
  );
};

export default ProductInventory;
